import type { BootConfig } from "./BootConfig";
import { FixtureOperatorIdMode } from "./launch/LaunchContextLoader";
import { BackendMode } from "./utils/backendUrl";
import { LaunchContextSourceMode, type LaunchContextFixture } from "./utils/launchContextSource";
import { readQueryParam } from "./utils/readQueryParam";

/**
 * A title's build environment as a plain record — what Vite's `import.meta.env` is, without the shell
 * depending on Vite's types. The names the shell reads are listed on `createBootConfig`.
 */
export type BootEnv = Readonly<Record<string, string | boolean | undefined>>;

/** What a title states about its boot; everything else the shell reads from the env and the page URL. */
export interface BootConfigDefaults {
  /** The deployed backend when the env names none: the `?backend=staging` target of every non-`deployed` build. */
  stagingBackendUrl: string;
  /** The offline launch context (the device shape) a fixture boot starts with; `?device=` overrides it. */
  launchContextFixture: LaunchContextFixture;
  /** Where `manifest.json` and the bundles are served from. */
  assetsBaseUrl: string;
  /** Minor units per display unit (100 for cents). */
  denominationFactor: number;
  /** QA: the offline launch context's operator. Default `None`. */
  fixtureOperatorIdMode?: FixtureOperatorIdMode;
  /** QA: "" (default) = real device detection. */
  forcedVisualProfile?: "low" | "high" | "";
  /** QA: "" (default) = the launch payload's locale. */
  localeCodeOverride?: string;
  /** QA: "" (default) = no operator override. */
  operatorIdOverride?: string;
}

/**
 * The `BootConfig` of a title's build. The env names it reads: `DEV` (Vite's dev server), `VITE_RELEASE` (`1`: a
 * release build, decision 83), `VITE_BACKEND_MODE` (`offline` | `localhost` | `deployed`; unset or empty = offline),
 * `VITE_DEPLOYED_URL`, `VITE_LOCALHOST_URL` (default `http://localhost:3000`), `VITE_OPERATOR_MANIFEST_BASE_URL`. Dev
 * mode is on for the dev server, and for `?dev=1` in any build but a release one: a public page's link can never turn
 * on the hook, the dev menu or the `?backend=` override; `?skipLoader=1` drops the loader in every build, `?intro=1`
 * keeps the intro splash in dev mode.
 *
 * A `deployed` build must name its backend: without `VITE_DEPLOYED_URL` (unset or empty) this throws,
 * inside the boot boundary (`startGame` calls it there), and the boot-failure popup shows (decision 31).
 * Every other build keeps the title's staging backend as the target of the dev override `?backend=staging`
 * (the dev server; `pnpm e2e:staging` boots the plain offline build with it).
 */
export function createBootConfig(input: { env: BootEnv; search: string; gameVersion: string; defaults: BootConfigDefaults }): BootConfig {
  const { env, search, defaults } = input;
  const devModeEnabled = env.DEV === true || (envString(env, "VITE_RELEASE") !== "1" && readQueryParam(search, "dev") === "1");
  const backendMode = parseBackendMode(envString(env, "VITE_BACKEND_MODE"));
  const deployedUrl = envString(env, "VITE_DEPLOYED_URL") ?? "";
  if (backendMode === BackendMode.Deployed && deployedUrl === "") throw new Error("createBootConfig: VITE_BACKEND_MODE=deployed needs VITE_DEPLOYED_URL");
  return {
    devModeEnabled,
    gameVersion: input.gameVersion,
    backendMode,
    localhostUrl: envString(env, "VITE_LOCALHOST_URL") ?? "http://localhost:3000",
    deployedUrl: deployedUrl || defaults.stagingBackendUrl,
    launchContextSourceMode: backendMode === BackendMode.Offline ? LaunchContextSourceMode.Fixture : LaunchContextSourceMode.RemoteUrl,
    launchContextFixture: defaults.launchContextFixture,
    fixtureOperatorIdMode: defaults.fixtureOperatorIdMode ?? FixtureOperatorIdMode.None,
    forcedVisualProfile: defaults.forcedVisualProfile ?? "",
    localeCodeOverride: defaults.localeCodeOverride ?? "",
    operatorIdOverride: defaults.operatorIdOverride ?? "",
    operatorManifestBaseUrl: envString(env, "VITE_OPERATOR_MANIFEST_BASE_URL") ?? "",
    assetsBaseUrl: defaults.assetsBaseUrl,
    denominationFactor: defaults.denominationFactor,
    skipLoader: readQueryParam(search, "skipLoader") === "1",
    skipIntro: devModeEnabled && readQueryParam(search, "intro") !== "1",
  };
}

function envString(env: BootEnv, key: string): string | undefined {
  const value = env[key];
  return typeof value === "string" ? value : undefined;
}

function parseBackendMode(raw: string | undefined): BackendMode {
  switch (raw) {
    case "localhost":
      return BackendMode.Localhost;
    case "deployed":
      return BackendMode.Deployed;
    case "offline":
    case undefined:
    case "":
      return BackendMode.Offline;
    default:
      console.warn(`[BootConfig] unknown VITE_BACKEND_MODE="${raw}" — using offline`);
      return BackendMode.Offline;
  }
}
